import { ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'

import { PageHeader, ServiceMark } from '@/components/ds'
import { EshopTabs } from '@/components/eshop/EshopTabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { EshopPaymentMethodForm } from '@/forms/EshopPaymentMethodForm/EshopPaymentMethodForm'
import { Collection } from '@/interfaces/Collection'
import { DeliveryMethod, EshopPaymentMethod } from '@/interfaces/eshop'
import { Settings } from '@/interfaces/Settings'
import { components } from '@invo.space/shared'

import { EnabledBadge, EshopPrices, SkeletonRows } from './components/EshopParts'

type InvoicingBankAccount = components['schemas']['InvoicingBankAccount']

export default function EshopPaymentMethodsPage() {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'eshop.paymentMethods',
  })
  const { t: te } = useTranslation(undefined, {
    keyPrefix: 'eshop',
  })

  const {
    data: methods,
    isLoading,
    mutate,
  } = useSWR<Collection<EshopPaymentMethod>>(
    '/api/v2/payment-methods',
    apiFetcher,
  )

  const { data: settings } = useSWR<Settings>('/api/v2/settings', apiFetcher)

  const { data: deliveryMethods } = useSWR<Collection<DeliveryMethod>>(
    '/api/v2/delivery-methods',
    apiFetcher,
  )

  const { data: bankAccounts } = useSWR<{ data?: InvoicingBankAccount[] }>(
    '/api/v2/bank-accounts',
    apiFetcher,
  )

  const currencies = [
    settings?.primaryCurrency ?? 'CZK',
    ...(settings?.alternativeCurrencies ?? []),
  ]

  const personalPickupEnabled =
    deliveryMethods?.data?.some(
      (method) => method.type === 'personalPickup' && method.isEnabled,
    ) ?? false

  const [editingMethod, setEditingMethod] = useState<EshopPaymentMethod | null>(
    null,
  )
  const [dialogOpen, setDialogOpen] = useState(false)

  const openEditDialog = (method: EshopPaymentMethod) => {
    setEditingMethod(method)
    setDialogOpen(true)
  }

  useSetPageHeader({
    breadcrumbs: [
      { href: '/eshop/payment-methods', children: te('title') },
      { href: '/eshop/payment-methods', children: t('title') },
    ],
  })

  const methodDetail = (method: EshopPaymentMethod) => {
    if (method.type === 'bankTransfer') {
      const account = bankAccounts?.data?.find((item) => item.id === method.invoicingBankAccountId)
      return account ? (
        <span>{account.name}</span>
      ) : (
        <span className="text-muted-foreground">{t('noBankAccountLinked')}</span>
      )
    }

    if (method.type === 'cardPayment') {
      return method.cardProviderKey ? (
        <span>{t('providerKey', { key: method.cardProviderKey })}</span>
      ) : (
        <span className="text-muted-foreground">Bez platební brány</span>
      )
    }

    if (method.type === 'payOnPickup' && !personalPickupEnabled) {
      return <span className="text-muted-foreground">Vyžaduje zapnutý osobní odběr</span>
    }

    return <span className="text-muted-foreground">-</span>
  }

  return (
    <>
      <title>{te('title') + ', ' + t('title')}</title>

      <div className="mx-auto w-full max-w-[1280px]">
        <EshopTabs />
        <PageHeader title={t('title')} description={t('description')} />

        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Platba</TableHead>
                <TableHead className="hidden md:table-cell">Nastavení</TableHead>
                <TableHead className="text-right md:w-44">Poplatek</TableHead>
                <TableHead className="hidden w-32 md:table-cell">Stav</TableHead>
                <TableHead className="hidden w-10 md:table-cell" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <SkeletonRows rows={4} cols={5} mobileCols={[0, 2]} />}
              {methods?.data?.map((method) => (
                <TableRow key={method.type} onClick={() => openEditDialog(method)} className="group cursor-pointer">
                  <TableCell className="w-full max-w-0 font-medium md:w-auto md:max-w-none">
                    <span className="flex items-center gap-3">
                      <ServiceMark service={method.type} />
                      <span className="hidden md:contents">{t(`types.${method.type}`)}</span>
                      <span className="min-w-0 md:hidden">
                        <span className="block truncate">{t(`types.${method.type}`)}</span>
                        <span className="block truncate text-xs font-normal text-muted-foreground">
                          {[!method.isEnabled ? 'Vypnuto' : null, methodDetail(method)].filter(Boolean).join(', ')}
                        </span>
                      </span>
                    </span>
                  </TableCell>
                  <TableCell className="hidden max-w-0 truncate md:table-cell">{methodDetail(method)}</TableCell>
                  <TableCell className="text-right">
                    <EshopPrices prices={method.prices} />
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <EnabledBadge enabled={method.isEnabled} />
                  </TableCell>
                  <TableCell className="hidden text-right md:table-cell">
                    <ChevronRight className="ml-auto size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <EshopPaymentMethodForm
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        method={editingMethod}
        currencies={currencies}
        bankAccounts={bankAccounts?.data ?? []}
        personalPickupEnabled={personalPickupEnabled}
        onSuccess={() => mutate()}
      />
    </>
  )
}
